import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  MessageSquare, 
  Send, 
  Image, 
  Paperclip, 
  Download, 
  FileText, 
  User, 
  Bot,
  Loader2,
  Trash2,
  Copy,
  BookOpen,
  Upload,
  RefreshCw,
  Eye,
  AlertCircle
} from 'lucide-react';

// Form validation schema - simplified (removed term/week fields - now auto-populated from active term)
const lessonNoteSchema = z.object({
  education_level: z.string().min(1, "Education level is required"),
  class_name: z.string().min(1, "Class name is required"),
  subject: z.string().min(1, "Subject is required")
});

const HybridLessonGenerator = () => {
  const [currentStep, setCurrentStep] = useState('form'); // 'form', 'chat', 'generated'
  const [formData, setFormData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [generatedLessonNote, setGeneratedLessonNote] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadLogs, setShowUploadLogs] = useState(false);
  const [uploadedNotes, setUploadedNotes] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTerm, setActiveTerm] = useState(null);
  const [loadingTerm, setLoadingTerm] = useState(true);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const form = useForm({
    resolver: zodResolver(lessonNoteSchema),
    defaultValues: {
      education_level: "",
      class_name: "",
      subject: ""
    }
  });

  // Fetch active term on component mount
  useEffect(() => {
    const fetchActiveTerm = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('auth_token');
        
        if (!token) {
          console.log('No auth token found in localStorage');
          setLoadingTerm(false);
          return;
        }
        
        const response = await fetch(`${backendUrl}/api/admin/terms/active`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Fetched active term:', data);
          setActiveTerm(data.term);
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Failed to fetch active term:', response.status, errorData);
        }
      } catch (error) {
        console.error('❌ Error fetching active term:', error);
      } finally {
        setLoadingTerm(false);
      }
    };
    
    fetchActiveTerm();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Get all subjects for the education level
  const getAllSubjectsForLevel = (educationLevel) => {
    switch (educationLevel) {
      case 'jhs':
        return ['Mathematics', 'English Language', 'Integrated Science', 'Social Studies', 'Religious and Moral Education', 'Computing', 'Creative Arts', 'Ghanaian Language', 'French', 'Career Technology'];
      case 'primary':
        return ['English Language', 'Ghanaian Language', 'Mathematics', 'Science', 'Computing', 'History', 'Creative Arts', 'Religious & Moral Education', 'French'];
      case 'lower_primary':
        return ['English Language', 'Ghanaian Language', 'Mathematics', 'Science', 'Computing', 'History', 'Creative Arts', 'Religious & Moral Education', 'French'];
      case 'kg':
        return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
      default:
        return [];
    }
  };

  // Get classes for the education level
  const getClassesForLevel = (educationLevel) => {
    switch (educationLevel) {
      case 'jhs':
        return ['JHS 1', 'JHS 2', 'JHS 3'];
      case 'primary':
        return ['Class 4', 'Class 5', 'Class 6'];
      case 'lower_primary':
        return ['Class 1', 'Class 2', 'Class 3'];
      case 'kg':
        return ['KG 1', 'KG 2'];
      default:
        return [];
    }
  };

  // Watch for education level changes to reset class and subject
  const watchedEducationLevel = form.watch('education_level');
  
  useEffect(() => {
    if (watchedEducationLevel) {
      form.setValue('class_name', '');
      form.setValue('subject', '');
    }
  }, [watchedEducationLevel, form]);

  // Note: KG/Primary now work the same as JHS - individual note uploads
  // ZIP folder logic has been removed to prevent database pressure

  // Helper function to normalize education level for backend
  const normalizeEducationLevel = (level) => {
    if (!level) return level;
    const normalized = level.toLowerCase().trim();
    // Map "lower primary" and "upper primary" to "primary"
    if (normalized.includes('primary')) return 'primary';
    if (normalized.includes('kg')) return 'kg';
    if (normalized.includes('jhs')) return 'jhs';
    return normalized;
  };

  // Note: ZIP folder functionality removed - all education levels now use individual uploads

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const onFormSubmit = (data) => {
    // Validate that we have active term with required metadata
    if (!activeTerm) {
      toast.error("No active term found. Please set an active term in Term Management.");
      return;
    }
    
    if (!activeTerm.academic_year || !activeTerm.current_week || !activeTerm.week_ending_date) {
      toast.error("Please set Academic Year, Week Number, and Week Ending Date in Term Management first.");
      return;
    }

    // Get term number from term name
    const termNumber = activeTerm.name.includes('1') ? '1' : activeTerm.name.includes('2') ? '2' : '3';

    // Merge form data with active term metadata
    const fullData = {
      ...data,
      week: activeTerm.current_week,
      term: termNumber,
      academic_year: activeTerm.academic_year,
      week_ending: activeTerm.week_ending_date
    };
    
    console.log('Form submitted with term data:', fullData);
    setFormData(fullData);
    
    // Note: All education levels now use individual uploads (ZIP folder logic removed)
    
    setCurrentStep('chat');
    
    // Initialize chat with lesson context
    const initialMessage = {
      id: 1,
      type: 'bot',
      content: `Hello! I'm ready to create your lesson note with these details:

**Education Level:** ${fullData.education_level.toUpperCase()}
**Class:** ${fullData.class_name}
**Subject:** ${fullData.subject}
**Week:** ${fullData.week}
**Term:** ${activeTerm.name}
**Academic Year:** ${fullData.academic_year}
**Week Ending:** ${fullData.week_ending}

Now please provide the curriculum details and any additional instructions:

**Required Information:**
• **Strand** (main topic area)
• **Sub-strand** (specific sub-topic) 
• **Content Standard** (what students should achieve)
• **Indicator** (specific learning outcome)

**Example:**
"Create a lesson on Numbers strand, Number Operations sub-strand, with content standard 'Demonstrate understanding of place value' and indicator 'Identify place value of digits up to thousands'"

You can also upload relevant images or documents to enhance the lesson. Type your complete lesson requirements below.`,
      timestamp: new Date()
    };
    
    setMessages([initialMessage]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
    
    toast.success(`Files uploaded: ${newFiles.length} file(s) ready to send`);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSendMessage = async (isQuickGenerate = false) => {
    if (!isQuickGenerate && !currentMessage.trim() && uploadedFiles.length === 0) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: isQuickGenerate ? "Generate lesson note with standard structure" : currentMessage,
      files: uploadedFiles,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setUploadedFiles([]);
    setIsGenerating(true);

    try {
      await generateLessonNoteWithAI(userMessage.content, uploadedFiles);
    } catch (error) {
      console.error('Error generating lesson note:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: `I apologize, but I encountered an error while generating your lesson note: ${error.message}. Please try again or contact support if the issue persists.`,
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateLessonNoteWithAI = async (prompt, files) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    // Create structured prompt combining form data and user message
    const structuredPrompt = `
Create a comprehensive lesson note for Ghana Education Service with these specifications:

LESSON DETAILS:
- Education Level: ${formData.education_level.toUpperCase()}
- Class: ${formData.class_name}
- Subject: ${formData.subject}
- Week: ${formData.week}
- Term: ${formData.term}
- Academic Year: ${formData.academic_year}
- Week Ending: ${formData.week_ending}

CURRICULUM SPECIFICATIONS AND ADDITIONAL INSTRUCTIONS:
${prompt}

Please create a complete lesson note following the GES Standard-Based Curriculum structure with all required components including proper strand, sub-strand, content standard, and indicator based on the user's instructions above. Include Ghana-specific examples and make it culturally relevant. IMPORTANT: Include the Week Ending date (${formData.week_ending}) prominently in the generated lesson note.
    `;

    try {
      // Create form data for file uploads
      const formDataToSend = new FormData();
      formDataToSend.append('prompt', structuredPrompt);
      
      // Add lesson metadata for database record creation
      // Normalize education_level for backend (primary/lower primary/upper primary → primary)
      formDataToSend.append('education_level', normalizeEducationLevel(formData.education_level));
      formDataToSend.append('class_name', formData.class_name);
      formDataToSend.append('week', formData.week.toString());
      formDataToSend.append('term', formData.term);
      formDataToSend.append('academic_year', formData.academic_year);
      formDataToSend.append('week_ending', formData.week_ending);
      
      if (formData.subject) {
        formDataToSend.append('subject', formData.subject);
      }
      
      files.forEach((fileInfo, index) => {
        formDataToSend.append(`files`, fileInfo.file);
      });

      // Get JWT token from localStorage for authentication
      const token = localStorage.getItem('auth_token');

      // Use streaming endpoint to avoid 60-second timeout
      const response = await fetch(`${backendUrl}/api/admin/generate-lesson-notes-stream`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Backend error:', errorData);
          throw new Error(errorData.detail || 'Failed to generate lesson notes');
        } else {
          console.error('Server error:', response.status);
          throw new Error(`Server error (${response.status}). Please try again.`);
        }
      }

      // Handle SSE streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let noteId = null;
      let buffer = '';

      // Create a temporary bot message that will be updated with streaming content
      const tempBotMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: '',
        timestamp: new Date(),
        isStreaming: true
      };
      setMessages(prev => [...prev, tempBotMessage]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete SSE messages (ending with \n\n)
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || ''; // Keep incomplete message in buffer
        
        for (const message of messages) {
          const lines = message.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              
              // Check if it's the completion event
              if (data.includes('"done":true') || data.includes('"done": true')) {
                try {
                  const completionData = JSON.parse(data);
                  noteId = completionData.id;
                } catch (e) {
                  // Not JSON, just regular content
                }
              } else {
                // Regular content chunk - decode escaped newlines back to actual newlines
                const decodedData = data.replace(/\\n/g, '\n');
                fullContent += decodedData;
                
                // Update the streaming message in real-time
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIndex = updated.length - 1;
                  if (lastIndex >= 0 && updated[lastIndex].isStreaming) {
                    updated[lastIndex] = {
                      ...updated[lastIndex],
                      content: fullContent
                    };
                  }
                  return updated;
                });
              }
            } else if (line.startsWith('event: complete')) {
              continue;
            } else if (line.startsWith('event: error')) {
              continue;
            }
          }
        }
      }

      // Finalize the bot message
      const result = {
        id: noteId || Date.now().toString(),
        lesson_content: fullContent,
        prompt: prompt,
        files_count: files.length,
        generated_at: new Date().toISOString(),
        success: true
      };

      // Update the final message
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (lastIndex >= 0) {
          updated[lastIndex] = {
            id: Date.now() + 1,
            type: 'bot',
            content: fullContent,
            timestamp: new Date(),
            lessonNote: result,
            canDownload: true,
            isStreaming: false
          };
        }
        return updated;
      });

      setGeneratedLessonNote(result);
      setCurrentStep('generated');
      
      toast.success(`✅ ${formData.subject} Lesson Note Generated! Ready for direct upload to teacher access.`);
      
    } catch (error) {
      console.error('Error in generateLessonNoteWithAI:', error);
      throw error;
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleRewrite = () => {
    setCurrentStep('chat');
    const rewriteMessage = {
      id: Date.now(),
      type: 'bot',
      content: `I'll help you rewrite the lesson note. Please tell me what changes you'd like me to make:

• Add more activities
• Change the difficulty level  
• Include more examples
• Modify the assessment questions
• Add multimedia resources
• Or any other specific improvements

What would you like me to adjust in the lesson note?`,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, rewriteMessage]);
  };

  // FIXED uploadToTeacherAccess with full metadata
  const uploadToTeacherAccess = async (note) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

      // Get JWT token from localStorage and validate
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        toast.error("❌ Authentication error: Please log in again to continue.");
        console.error('No auth token found in localStorage');
        return;
      }

      // Validate token format (JWT should have 3 segments separated by dots)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        toast.error("❌ Authentication error: Invalid session. Please log in again.");
        console.error('Invalid token format:', token.substring(0, 20) + '...');
        // Clear invalid token
        localStorage.removeItem('auth_token');
        return;
      }

      const payload = {
        lesson_note_id: note.id,
        class_name: note.class_name || formData.class_name,
        subject: note.subject || formData.subject,
        education_level: normalizeEducationLevel(note.education_level || formData.education_level),
        term: note.term || formData.term,
        week: note.week || formData.week,
        academic_year: note.academic_year || formData.academic_year,
        week_ending: note.week_ending || formData.week_ending,
        file_urls: note.pdf_urls || [], // include generated file links
        strand: note.strand,
        sub_strand: note.sub_strand,
        content_standard: note.content_standard,
        indicator: note.indicator,
        lesson_content: note.lesson_content || note.content || '', // CRITICAL: Include lesson content for PDF generation
      };

      console.log('📤 Uploading to teachers with payload:', {
        ...payload,
        lesson_content: payload.lesson_content ? `${payload.lesson_content.substring(0, 100)}... (${payload.lesson_content.length} chars)` : 'MISSING'
      });
      console.log('🔑 Using auth token:', token.substring(0, 30) + '...');

      const response = await fetch(`${backendUrl}/api/admin/upload-lesson-to-teachers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Clone response so we can read it multiple times if needed
        const responseClone = response.clone();
        let errorMessage = 'Failed to upload lesson note';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (jsonError) {
          // Response is not JSON, try to get text from the clone
          try {
            const errorText = await responseClone.text();
            console.error('Non-JSON error response:', errorText.substring(0, 200));
            errorMessage = response.status === 401 
              ? 'Authentication failed. Please log in again.' 
              : `Server error (${response.status}): ${errorText.substring(0, 100)}`;
          } catch (textError) {
            // If both fail, use status-based message
            errorMessage = response.status === 401 
              ? 'Authentication failed. Please log in again.' 
              : `Server error (${response.status})`;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Upload successful:', result);
      toast.success("✅ Lesson note uploaded successfully. Teachers can now access it.");

      // Refresh admin upload logs
      // loadUploadedNotes();

    } catch (error) {
      console.error('Error uploading lesson note:', error);
      toast.error(`❌ Failed to upload lesson note to teacher access: ${error.message}`);
    }
  };

  const handleUploadToTeachers = async () => {
    if (!generatedLessonNote || !generatedLessonNote.id) {
      toast.error("No lesson note available to upload. Please generate a lesson note first.");
      return;
    }

    if (!formData) {
      toast.error("Form data is missing. Please refresh and try again.");
      return;
    }

    setIsUploading(true);
    try {
      await uploadToTeacherAccess(generatedLessonNote);
      
      setUploadedNotes(prev => [...prev, {
        id: generatedLessonNote.id,
        class_name: formData.class_name,
        subject: formData.subject,
        education_level: formData.education_level,
        uploadedAt: new Date().toISOString()
      }]);

    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Note: Class folder download functionality removed

  // Note: ZIP folder upload functionality removed

  const startOver = () => {
    setCurrentStep('form');
    setMessages([]);
    setGeneratedLessonNote(null);
    setFormData(null);
    form.reset();
  };

  // Form Step
  if (currentStep === 'form') {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              AI Lesson Note Generator
            </CardTitle>
            <CardDescription>
              Fill out the basic lesson information, then use AI chat to create comprehensive lesson notes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onFormSubmit, (errors) => {
              console.log('Form validation errors:', errors);
              toast.error('Please fill in all required fields before continuing');
            })} className="space-y-6">
              {/* Education Level Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Education Level</label>
                <Controller
                  name="education_level"
                  control={form.control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className={form.formState.errors.education_level ? "border-red-500" : ""}>
                        <SelectValue placeholder="Select education level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="jhs">JHS (Junior High School)</SelectItem>
                        <SelectItem value="primary">Upper Primary (Class 4-6)</SelectItem>
                        <SelectItem value="lower_primary">Lower Primary (Class 1-3)</SelectItem>
                        <SelectItem value="kg">KG (Kindergarten)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {form.formState.errors.education_level && (
                  <p className="text-sm text-red-500 mt-1">{form.formState.errors.education_level.message}</p>
                )}
              </div>

              {/* Class and Subject Selection */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Class</label>
                  <Controller
                    name="class_name"
                    control={form.control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value} disabled={!form.watch('education_level')}>
                        <SelectTrigger className={form.formState.errors.class_name ? "border-red-500" : ""}>
                          <SelectValue placeholder={form.watch('education_level') ? "Select class" : "Select education level first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {/* JHS Classes */}
                          {form.watch('education_level') === 'jhs' && (
                            <>
                              <SelectItem value="JHS 1">JHS 1</SelectItem>
                              <SelectItem value="JHS 2">JHS 2</SelectItem>
                              <SelectItem value="JHS 3">JHS 3</SelectItem>
                            </>
                          )}
                          
                          {/* Upper Primary Classes (Class 4-6) */}
                          {form.watch('education_level') === 'primary' && (
                            <>
                              <SelectItem value="Class 4">Class 4</SelectItem>
                              <SelectItem value="Class 5">Class 5</SelectItem>
                              <SelectItem value="Class 6">Class 6</SelectItem>
                            </>
                          )}
                          
                          {/* Lower Primary Classes (Class 1-3) */}
                          {form.watch('education_level') === 'lower_primary' && (
                            <>
                              <SelectItem value="Class 1">Class 1</SelectItem>
                              <SelectItem value="Class 2">Class 2</SelectItem>
                              <SelectItem value="Class 3">Class 3</SelectItem>
                            </>
                          )}
                          
                          {/* KG Classes */}
                          {form.watch('education_level') === 'kg' && (
                            <>
                              <SelectItem value="KG 1">KG 1</SelectItem>
                              <SelectItem value="KG 2">KG 2</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {form.formState.errors.class_name && (
                    <p className="text-sm text-red-500 mt-1">{form.formState.errors.class_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {form.watch('education_level') === 'kg' ? 'Day' : 'Subject'}
                  </label>
                  <Controller
                    name="subject"
                    control={form.control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className={form.formState.errors.subject ? "border-red-500" : ""}>
                          <SelectValue placeholder={form.watch('education_level') === 'kg' ? "Select day" : "Select subject"} />
                        </SelectTrigger>
                        <SelectContent>
                          {/* JHS Subjects */}
                          {form.watch('education_level') === 'jhs' && (
                            <>
                              <SelectItem value="Mathematics">Mathematics</SelectItem>
                              <SelectItem value="English Language">English Language</SelectItem>
                              <SelectItem value="Integrated Science">Integrated Science</SelectItem>
                              <SelectItem value="Social Studies">Social Studies</SelectItem>
                              <SelectItem value="Religious and Moral Education">Religious and Moral Education</SelectItem>
                              <SelectItem value="Computing">Computing</SelectItem>
                              <SelectItem value="Creative Arts">Creative Arts</SelectItem>
                              <SelectItem value="Ghanaian Language">Ghanaian Language</SelectItem>
                              <SelectItem value="French">French</SelectItem>
                              <SelectItem value="Career Technology">Career Technology</SelectItem>
                            </>
                          )}
                          
                          {/* Upper Primary Subjects (Class 4-6) */}
                          {form.watch('education_level') === 'primary' && (
                            <>
                              <SelectItem value="English Language">English Language</SelectItem>
                              <SelectItem value="Ghanaian Language">Ghanaian Language</SelectItem>
                              <SelectItem value="Mathematics">Mathematics</SelectItem>
                              <SelectItem value="Science">Science</SelectItem>
                              <SelectItem value="Computing">Computing</SelectItem>
                              <SelectItem value="History">History</SelectItem>
                              <SelectItem value="Creative Arts">Creative Arts</SelectItem>
                              <SelectItem value="Religious & Moral Education">Religious & Moral Education</SelectItem>
                              <SelectItem value="French">French</SelectItem>
                            </>
                          )}
                          
                          {/* Lower Primary Subjects (Class 1-3) */}
                          {form.watch('education_level') === 'lower_primary' && (
                            <>
                              <SelectItem value="English Language">English Language</SelectItem>
                              <SelectItem value="Ghanaian Language">Ghanaian Language</SelectItem>
                              <SelectItem value="Mathematics">Mathematics</SelectItem>
                              <SelectItem value="Science">Science</SelectItem>
                              <SelectItem value="Computing">Computing</SelectItem>
                              <SelectItem value="History">History</SelectItem>
                              <SelectItem value="Creative Arts">Creative Arts</SelectItem>
                              <SelectItem value="Religious & Moral Education">Religious & Moral Education</SelectItem>
                              <SelectItem value="French">French</SelectItem>
                            </>
                          )}
                          
                          {/* KG Days (for weekly lesson notes) */}
                          {form.watch('education_level') === 'kg' && (
                            <>
                              <SelectItem value="Monday">Monday</SelectItem>
                              <SelectItem value="Tuesday">Tuesday</SelectItem>
                              <SelectItem value="Wednesday">Wednesday</SelectItem>
                              <SelectItem value="Thursday">Thursday</SelectItem>
                              <SelectItem value="Friday">Friday</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {form.formState.errors.subject && (
                    <p className="text-sm text-red-500 mt-1">{form.formState.errors.subject.message}</p>
                  )}
                </div>
              </div>
              
              {/* All education levels now work the same way */}

              {/* Active Term Settings Display - Auto-populated from Term Management */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Lesson Note Settings (Auto-populated from Term Management)</span>
                </div>
                
                {loadingTerm ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading term settings...
                  </div>
                ) : activeTerm ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 block">Term</span>
                      <span className="font-medium text-gray-900">{activeTerm.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Academic Year</span>
                      <span className="font-medium text-gray-900">
                        {activeTerm.academic_year || <span className="text-orange-600">Not set</span>}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Week</span>
                      <span className="font-medium text-gray-900">
                        {activeTerm.current_week ? `Week ${activeTerm.current_week}` : <span className="text-orange-600">Not set</span>}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Week Ending</span>
                      <span className="font-medium text-gray-900">
                        {activeTerm.week_ending_date || <span className="text-orange-600">Not set</span>}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-orange-600">
                    <AlertCircle className="h-4 w-4" />
                    No active term found. Please activate a term in Term Management.
                  </div>
                )}
                
                {activeTerm && (!activeTerm.academic_year || !activeTerm.current_week || !activeTerm.week_ending_date) && (
                  <p className="text-orange-600 text-xs mt-2 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Please complete all settings in Term Management before generating lesson notes.
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={!activeTerm || !activeTerm.academic_year || !activeTerm.current_week || !activeTerm.week_ending_date}
              >
                Continue to AI Chat
                <MessageSquare className="h-4 w-4 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Chat Step  
  if (currentStep === 'chat') {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="h-[80vh] flex flex-col">
          <CardHeader className="flex-shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  AI Lesson Notes Assistant
                </CardTitle>
                <CardDescription>
                  Creating lesson note for {formData?.class_name} - {formData?.subject} - Week {formData?.week}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleSendMessage(true)}>
                  <Send className="h-4 w-4 mr-2" />
                  Generate Lesson Note
                </Button>
                <Button variant="outline" size="sm" onClick={startOver}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Start Over
                </Button>
              </div>
            </div>
          </CardHeader>

          {/* Messages Area */}
          <CardContent className="flex-1 flex flex-col overflow-hidden p-0">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.type === 'user'
                        ? 'bg-blue-500 text-white'
                        : message.isError
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {message.type === 'user' ? (
                        <User className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Bot className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium mb-1">
                          {message.type === 'user' ? 'You' : 'AI Assistant'}
                        </div>
                        <div className="text-sm whitespace-pre-wrap leading-relaxed">
                          {message.content}
                        </div>

                        {/* File attachments */}
                        {message.files && message.files.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {message.files.map((file) => (
                              <div key={file.id} className="flex items-center gap-2 text-xs">
                                <Paperclip className="h-3 w-3" />
                                <span>{file.name}</span>
                                {file.preview && (
                                  <img 
                                    src={file.preview} 
                                    alt={file.name} 
                                    className="h-8 w-8 object-cover rounded"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Action buttons for bot messages with lesson notes */}
                        {message.type === 'bot' && message.canDownload && (
                          <div className="mt-3 flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText(message.content)}
                              className="text-xs"
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs opacity-70 text-right">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isGenerating && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4" />
                      <div className="text-sm">AI Assistant</div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-gray-600">
                        Generating lesson notes...
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* File Preview Area */}
            {uploadedFiles.length > 0 && (
              <div className="border-t bg-gray-50 p-3">
                <div className="flex flex-wrap gap-2">
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-2 bg-white rounded-lg p-2 text-sm border"
                    >
                      {file.preview ? (
                        <img 
                          src={file.preview} 
                          alt={file.name} 
                          className="h-8 w-8 object-cover rounded"
                        />
                      ) : (
                        <FileText className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="truncate max-w-32">{file.name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFile(file.id)}
                        className="h-6 w-6 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Provide additional instructions for the lesson note... (Press Enter to send, Shift+Enter for new line)"
                    className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    disabled={isGenerating}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isGenerating}
                    className="px-3"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isGenerating}
                    className="px-3"
                  >
                    <Image className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => handleSendMessage()}
                    disabled={isGenerating || (!currentMessage.trim() && uploadedFiles.length === 0)}
                    className="px-4"
                    size="sm"
                  >
                    {isGenerating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Generated Step with Functional Tabs
  if (currentStep === 'generated') {

    return (
      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Generated Lesson Note
                </CardTitle>
                <CardDescription>
                  {formData.class_name} - {formData.subject} - Week {formData.week} - Term {formData.term}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={startOver}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Create New
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Functional Tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
              >
                <Eye className="h-3 w-3 mr-1" />
                Preview
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleRewrite}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Rewrite
              </Button>

              {/* All education levels now use individual uploads */}
              <div className="flex flex-col gap-2 bg-green-50 p-3 rounded-lg w-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-800">Individual PDF Ready</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleUploadToTeachers}
                    disabled={!generatedLessonNote?.id || isUploading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isUploading ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3 mr-1" />
                    )}
                    Upload to Teachers
                  </Button>
                </div>
                <div className="text-sm text-green-700">
                  File: {formData.class_name.replace(/\s+/g, '_')}-Week{formData.week}-{formData.subject.replace(/\s+/g, '_')}-lesson_note.pdf
                </div>
                <div className="text-xs text-green-600">
                  Click &quot;Upload to Teachers&quot; to make this lesson note available to subscribed teachers
                </div>
              </div>
            </div>

            {/* Content Preview */}
            <div className="bg-gray-50 p-6 rounded-lg max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                {generatedLessonNote?.lesson_content || 'No content available'}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Lesson Note Preview</DialogTitle>
              <DialogDescription>
                {formData.class_name} - Week {formData.week} - Term {formData.term}
              </DialogDescription>
            </DialogHeader>
            <div className="bg-white p-6 rounded-lg">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                {generatedLessonNote?.lesson_content || 'No content available'}
              </pre>
            </div>
            <DialogFooter className="flex justify-between items-center">
              <div className="text-sm text-gray-600 flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                <span className="font-medium">File:</span>
                <span className="ml-1">
                  {`${formData.class_name.replace(/\s+/g, '_')}_${formData.subject.replace(/\s+/g, '_')}_week${formData.week}_term${formData.term}.pdf`}
                </span>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleUploadToTeachers}
                  disabled={!generatedLessonNote?.id || isUploading}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {isUploading ? 'Uploading...' : 'Upload to Teachers'}
                </Button>
                <Button onClick={() => setShowPreview(false)} variant="outline">
                  Close
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
};

export default HybridLessonGenerator;